import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import Container from 'brastrap/common/container/Container';
import ProductSummary from 'brastrap/promotional/product-summary/ProductSummary';

const MODAL_PREFIX = 'field-brasize-accessories.modal';

const AccessoriesContainer = props => {
  const {
    activeModal,
    briefsSizeGuide,
    currency,
    disableRating,
    displayInline,
    onSizeSelect,
    onAddToBag,
    onModalOpen,
    onModalClose,
    onStyleColourChange,
    customerDetails,
    socialMediaChannels,
    isDesktop,
    pdpNavigableAccessories: {
      sportsWearProductGroups,
      swimSuitsProductGroups,
    },
  } = props;
  const containerProps = { ...props };
  delete containerProps.styleColours;
  delete containerProps.onAddToBag;
  delete containerProps.onSizeSelect;
  delete containerProps.matchingStyles;
  delete containerProps.bagSkuCodes;

  if (!props.styleColours.length) return null;

  const [viewableStyleColours, setViewableStyleColours] = useState(null);
  const [viewAllItems, setViewAllItems] = useState(false);

  useEffect(() => {
    if (props.styleColours.length) {
      setViewableStyleColours(props.styleColours.slice(0, 3));
    }
  }, []);

  useEffect(() => {
    if (viewAllItems) {
      setViewableStyleColours(props.styleColours);
    } else {
      setViewableStyleColours(props.styleColours.slice(0, 3));
    }
  }, [viewAllItems]);

  return (
    <Container adjacentItem={briefsSizeGuide} noBodyMargins {...containerProps}>
      <div className="c-accessories">
        <div className="c-accessories__main">
          {viewableStyleColours &&
            viewableStyleColours.map((style, index) => {
              const productSummary = { ...style };
              // Disable the add button if not enabled in the product's matching styles.
              const matching = props.matchingStyles.find(
                s =>
                  s.styleCode === style.styleCode &&
                  s.defaultStyleColourId === productSummary.defaultStyleColourId
              );
              productSummary.disableAdd = !matching.enabled;

              const modalId = `${MODAL_PREFIX}.${index}`;
              const isOpen = activeModal === modalId;

              const sizePicker = {
                onModalOpen,
                onModalClose,
                modalId,
                isOpen,
                value: matching.sku,
              };

              // If a bag API operation is ongoing, work out if it applies to this style code.
              const styleBeingFetched =
                props.fetchingSku &&
                props.matchingStyles.find(
                  s => s.enabled && s.sku === props.fetchingSku
                );
              const isFetching = styleBeingFetched
                ? style.styleCode === styleBeingFetched.styleCode &&
                  style.defaultStyleColourId ===
                    styleBeingFetched.defaultStyleColourId
                : false;

              const isAdded =
                matching.enabled &&
                props.bagSkuCodes.some(sku => sku === matching.sku);

              const currentProductCategory = props.productCategory.toUpperCase();
              const productGroup = props.productGroup.toUpperCase();

              const isProductCategoryNavigable = (
                productCategory,
                productGroups
              ) => {
                return (
                  currentProductCategory === productCategory &&
                  productGroups?.split(', ').includes(productGroup)
                );
              };

              let canNavigateToPDP;

              switch (currentProductCategory) {
                case 'NIGHTWEAR':
                  canNavigateToPDP = true;
                  break;
                case 'LINGERIE':
                  canNavigateToPDP = isProductCategoryNavigable(
                    currentProductCategory,
                    sportsWearProductGroups
                  );
                  break;
                case 'SWIMWEAR':
                  canNavigateToPDP = isProductCategoryNavigable(
                    currentProductCategory,
                    swimSuitsProductGroups
                  );
                  break;
                default:
                  canNavigateToPDP = false;
                  break;
              }

              return (
                <ProductSummary
                  {...productSummary}
                  currency={currency}
                  sizePicker={sizePicker}
                  onAddToBag={onAddToBag}
                  onSizeSelect={onSizeSelect}
                  key={index}
                  position={index}
                  modifiers={[displayInline ? 'inline' : 'block']}
                  disableLink={!canNavigateToPDP}
                  isFetching={isFetching}
                  isAdded={isAdded}
                  disableRating={disableRating}
                  displaySaveItemButton={false}
                  onStyleColourChange={onStyleColourChange}
                  isAccessoryContainer
                  customerDetails={customerDetails}
                  socialMediaChannels={socialMediaChannels}
                  hideSizeRange
                  showSwatchesAsDropDown
                  isDesktop={isDesktop}
                  isForPdp
                  id={`product-accessory-${index}`}
                />
              );
            })}
        </div>
      </div>
      {props.styleColours && props.styleColours.length > 3 && (
        <div className="c-accessories__showAllContainer">
          <span className="c-accessories__showAllAmountText">
            Displaying <b>{viewAllItems ? props.styleColours.length : 3}</b> out
            of <b>{props.styleColours.length}</b>
          </span>
          <button
            className="c-accessories__showAllOrLess"
            onClick={() => setViewAllItems(x => !x)}
          >
            {viewAllItems ? (
              <>
                <span className="c-accessories__showAllOrLessText">
                  Show less
                </span>
                <span className="c-accessories__showAllOrLessChevron">❮</span>
              </>
            ) : (
              <>
                <span className="c-accessories__showAllOrLessText">
                  Show all styles
                </span>
                <span className="c-accessories__showAllOrLessChevron">❯</span>
              </>
            )}
          </button>
        </div>
      )}
    </Container>
  );
};

AccessoriesContainer.propTypes = {
  ...Container.propTypes,

  activeModal: PropTypes.string,
  bagSkuCodes: PropTypes.arrayOf(PropTypes.string),
  briefsSizeGuide: PropTypes.node,
  currency: PropTypes.string,
  disableRating: PropTypes.bool,
  displayInline: PropTypes.bool,
  fetchingSku: PropTypes.string,
  matchingStyles: PropTypes.array,
  onAddToBag: PropTypes.func.isRequired,
  onSizeSelect: PropTypes.func,
  onModalOpen: PropTypes.func,
  onModalClose: PropTypes.func,
  onStyleColourChange: PropTypes.func,
  styleColours: PropTypes.arrayOf(PropTypes.shape(ProductSummary.propTypes))
    .isRequired,
  customerDetails: PropTypes.object,
  socialMediaChannels: PropTypes.array,
  isDesktop: PropTypes.bool,
};

AccessoriesContainer.defaultProps = {
  matchingStyles: [],
  modifiers: ['ornamented'],
  onAddToBag: () => {},
  styleColours: [],
  bagSkuCodes: [],
};

export default AccessoriesContainer;
